
import React, { useState, useEffect } from 'react';
import { format, parseISO, isToday, addDays } from 'date-fns';
import { Bell, ArrowLeft, AlertTriangle, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTimetableData } from '../hooks/useTimetableData';
import { enhancedToast } from '@/utils/notifications/enhancedToast';

const TimetableAlerts = () => {
  const { timetableData, isLoading, refreshData } = useTimetableData();
  const [alerts, setAlerts] = useState({ today: [], upcoming: [], issues: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  
  const updateAlerts = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      setAlerts({ today: [], upcoming: [], issues: [] });
      return;
    }
    
    const now = new Date();
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);
    
    const todayAlerts = [];
    const upcomingAlerts = [];
    const issueAlerts = [];
    
    data.forEach(teacher => {
      if (!teacher.classes || !Array.isArray(teacher.classes)) return;
      
      teacher.classes.forEach(classItem => {
        try {
          const classTime = parseISO(classItem.startTime);
          const alertObject = {
            ...classItem,
            teacherName: teacher.name,
            teacherId: teacher.id
          };
          
          // Check for issues (absent or late)
          if (classItem.status === 'absent' || classItem.status === 'late') {
            issueAlerts.push(alertObject);
          }
          
          // Today's classes
          if (isToday(classTime) && classTime > now && classTime <= todayEnd) {
            todayAlerts.push(alertObject);
          }
          // Upcoming classes (next 7 days but not today)
          else if (classTime > todayEnd && classTime <= addDays(todayEnd, 7)) {
            upcomingAlerts.push(alertObject);
          }
        } catch (error) {
          console.error('Error processing class item:', error, classItem);
        }
      });
    });
    
    // Sort by start time
    const sortByTime = (a, b) => {
      try {
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      } catch (error) {
        console.error('Error sorting class times:', error);
        return 0;
      }
    };
    
    setAlerts({
      today: todayAlerts.sort(sortByTime),
      upcoming: upcomingAlerts.sort(sortByTime),
      issues: issueAlerts.sort(sortByTime)
    });
    
    setLastRefreshed(new Date());
  };
  
  useEffect(() => {
    // Update alerts whenever timetable data changes
    updateAlerts(timetableData);
    
    // Set up polling interval for automatic refresh
    const intervalId = setInterval(() => {
      refreshData().then(() => {
        updateAlerts(timetableData);
      });
    }, 2 * 60 * 1000); // Every 2 minutes
    
    return () => clearInterval(intervalId);
  }, [timetableData, refreshData]);
  
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await refreshData();
      updateAlerts(timetableData);
      enhancedToast.success("Alerts updated successfully");
    } catch (error) {
      enhancedToast.error("Failed to update alerts");
      console.error("Error refreshing alerts:", error);
    } finally {
      setRefreshing(false);
    }
  };
  
  if (isLoading && !timetableData?.length) {
    return (
      <div className="container p-4 mx-auto">
        <div className="flex items-center mb-6">
          <Link to="/timetable">
            <Button variant="ghost" size="sm" className="mr-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold flex items-center">
            <Bell className="mr-2 h-6 w-6" />
            Timetable Alerts
          </h1>
        </div>
        <div className="p-8 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <RefreshCw className="h-8 w-8 animate-spin mb-4 text-primary" />
            <p>Loading alerts...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container p-4 mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/timetable">
            <Button variant="ghost" size="sm" className="mr-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold flex items-center">
            <Bell className="mr-2 h-6 w-6" />
            Timetable Alerts
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-muted-foreground">
            Last updated: {format(lastRefreshed, "HH:mm:ss")}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="today" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="today">
            Today's Classes
            {alerts.today.length > 0 && (
              <Badge className="ml-2 bg-primary">{alerts.today.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="issues">
            Issues
            {alerts.issues.length > 0 && (
              <Badge className="ml-2 bg-destructive">{alerts.issues.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            Upcoming
            {alerts.upcoming.length > 0 && (
              <Badge className="ml-2 bg-muted-foreground">{alerts.upcoming.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="today">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center">
                <CheckCircle className="mr-2 h-5 w-5" />
                Today's Classes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.today.length > 0 ? (
                <div className="space-y-3">
                  {alerts.today.map((alert, idx) => (
                    <AlertItem key={idx} alert={alert} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No more classes scheduled for today</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="issues">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                Issues Requiring Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.issues.length > 0 ? (
                <div className="space-y-3">
                  {alerts.issues.map((alert, idx) => (
                    <AlertItem key={idx} alert={alert} showDate />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No issues reported</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="upcoming">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center">
                <Bell className="mr-2 h-5 w-5" />
                Upcoming Classes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.upcoming.length > 0 ? (
                <div className="space-y-3">
                  {alerts.upcoming.map((alert, idx) => (
                    <AlertItem key={idx} alert={alert} showDate />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No upcoming classes in the next 7 days</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AlertItem = ({ alert, showDate = false }) => {
  // Safely handle status display
  const getStatusText = () => {
    if (typeof alert.status === 'object' && alert.status !== null) {
      return String(alert.status.status || '').toUpperCase();
    }
    return String(alert.status || '').toUpperCase();
  };

  return (
    <div className="flex justify-between items-start border p-3 rounded-md">
      <div>
        <div className="font-medium">{alert.subject}</div>
        <div className="text-sm text-muted-foreground">{alert.teacherName}</div>
        <div className="text-xs flex items-center mt-1 text-muted-foreground">
          <Clock className="h-3 w-3 mr-1" />
          {showDate 
            ? format(parseISO(alert.startTime), "EEE, MMM d • h:mm a") 
            : format(parseISO(alert.startTime), "h:mm a")}
          {" • Room "}
          {alert.room}
        </div>
      </div>
      <Badge 
        variant="outline" 
        className={`
          ${alert.status === 'absent' ? 'border-red-500 text-red-800 bg-red-50' : 
            alert.status === 'late' ? 'border-orange-500 text-orange-800 bg-orange-50' : 
            'border-green-500 text-green-800 bg-green-50'}
        `}
      >
        {getStatusText()}
      </Badge>
    </div>
  );
};

export default TimetableAlerts;
